/**
 * AI Image Tagging
 * Fügt automatisch ein "AI"-Badge (unten rechts) zu allen Bildern hinzu,
 * deren Dateiname vor der Endung auf "-ai" endet, z. B.:
 *   produktname-ai.jpg
 *   produktname-ai_600x600crop_center.jpg
 *   produktname-ai.png?v=123456
 *
 * PERFORMANCE-HINWEISE:
 * - Der initiale Scan läuft einmalig beim DOM-Ready über das gesamte Dokument.
 * - Danach werden NUR neu hinzugefügte DOM-Knoten geprüft (nicht erneut das
 *   gesamte Dokument), um unnötige querySelectorAll-Aufrufe zu vermeiden.
 * - Mehrere schnell aufeinanderfolgende DOM-Änderungen (z. B. durch Apps,
 *   Slider, Variantenwechsel) werden gebündelt und erst in einem
 *   Leerlaufmoment des Browsers (requestIdleCallback) verarbeitet, um den
 *   Main Thread nicht zu blockieren (TBT/INP-Schonung).
 * - getComputedStyle() wird ausschließlich für tatsächlich erkannte
 *   AI-Bilder aufgerufen, nicht für jedes Bild im Shop.
 *
 * Einbindung in Shopify:
 * 1. Diese Datei als "ai-image-tag.js" in Assets hochladen.
 * 2. In theme.liquid vor </body> einfügen:
 *    {{ 'ai-image-tag.js' | asset_url | script_tag }}
 * 3. ai-image-tag.css ebenfalls in Assets hochladen und einbinden
 *    (in theme.liquid <head> oder über {{ 'ai-image-tag.css' | asset_url | stylesheet_tag }}).
 */
(function () {
  // Erkennt "-ai" oder "_ai" (Groß-/Kleinschreibung egal) direkt vor der
  // Dateiendung. Danach sind beliebige weitere Zeichen erlaubt (Ziffern,
  // Buchstaben, Bindestriche, Unterstriche) – das deckt sowohl
  // Shopify-Resize-Suffixe (z. B. "_600x600") als auch automatisch von
  // Shopify vergebene Duplikat-Suffixe (z. B. "_1", "-1", "2") ab.
  var AI_PATTERN = /[-_]ai[a-z0-9_-]*\.(jpg|jpeg|png|webp|gif|avif)(\?|$)/i;

  // Bereiche, die von der AI-Tag-Logik ausgeschlossen werden sollen:
  // Navigations-/Menü-Bilder benötigen keine AI-Kennzeichnung (betrifft nur
  // Produkt-/Content-Bilder) UND enthalten oft JS-Logik (z. B. jQuery .next()),
  // die auf die exakte, unveränderte Geschwister-Struktur angewiesen ist.
  var EXCLUDED_SELECTORS = [
    '.lb-mobile-navigation',
    'header',
    'nav',
    '.menu-drawer',
    '.header-drawer',
    // AOV.ai Cart Drawer – laut App-Support bestätigter Container
    '#aov-cart-drawer'
  ];

  // requestIdleCallback-Fallback für Browser ohne Unterstützung (z. B. Safari < 2023)
  var scheduleIdle = window.requestIdleCallback
    ? function (cb) { window.requestIdleCallback(cb, { timeout: 500 }); }
    : function (cb) { setTimeout(cb, 100); };

  // Sucht beim Bild bzw. dessen Vorfahren nach einer bereits vorhandenen
  // border-bottom-right-radius, damit sich das Tag optisch an abgerundete
  // Bildecken anpasst (z. B. bei Hero-Bannern mit "abgerundet"-Einstellung).
  function findMatchingRadius(startEl) {
    var node = startEl;
    for (var i = 0; i < 8 && node && node !== document.body; i++) {
      var cs = window.getComputedStyle(node);
      var radius = cs.borderBottomRightRadius;
      if (radius && parseFloat(radius) > 0) {
        return radius;
      }
      node = node.parentElement;
    }
    return '0px';
  }

  function isExcluded(img) {
    for (var i = 0; i < EXCLUDED_SELECTORS.length; i++) {
      if (img.closest(EXCLUDED_SELECTORS[i])) return true;
    }
    return false;
  }

  // Sucht ab mediaBox aufwärts nach einem gemeinsamen Elternteil, der auch
  // ein Overlay-Element (Klassenname enthält "overlay", z. B. für die
  // Deckkraft-Überlagerung bei Slideshows/Bannern) als Nachfahren enthält.
  // Gibt diesen gemeinsamen Elternteil zurück, oder null, falls keiner
  // gefunden wurde. Das Tag wird dann als LETZTES Kind dort angehängt,
  // wodurch es automatisch die Stapelreihenfolge gegenüber dem Overlay
  // gewinnt (bei gleichem z-index gewinnt das später im DOM stehende
  // Element).
  function findOverlaySharedParent(mediaBox) {
    var node = mediaBox;
    for (var i = 0; i < 6 && node && node !== document.body; i++) {
      var parent = node.parentElement;
      if (!parent) return null;

      // Nur DIREKTE Kind-Elemente prüfen (nicht den kompletten Unterbaum),
      // damit kein unabhängiges, weit entferntes Overlay (z. B. ein
      // Modal-Backdrop tief in der Produktkarte) fälschlich als Treffer
      // gilt und das Tag an eine falsche Stelle setzt.
      for (var c = 0; c < parent.children.length; c++) {
        var child = parent.children[c];
        if (child !== node && child.className && typeof child.className === 'string' && /overlay/i.test(child.className)) {
          return parent;
        }
      }
      node = parent;
    }
    return null;
  }

  // Sucht ab startEl aufwärts nach Vorfahren, die einen eigenen CSS-
  // Stacking-Context erzeugen (transform, opacity<1, filter, will-change,
  // isolation) – typisch bei Animations-Komponenten (z. B. "motion-element"
  // in Slideshow-/Hero-Sektionen). Ein Badge INNERHALB so eines Vorfahren
  // kann per z-index nicht über Geschwister-Elemente wie ein Overlay
  // hinausragen, egal wie hoch der z-index gesetzt wird. Gibt den Elternteil
  // des am weitesten außen liegenden solchen Vorfahren zurück (oder null,
  // falls keiner gefunden wurde).
  function findStackingEscapeParent(startEl) {
    var node = startEl;
    var escapeParent = null;
    for (var i = 0; i < 10 && node && node !== document.body; i++) {
      var cs = window.getComputedStyle(node);
      var createsContext = (cs.transform && cs.transform !== 'none') ||
        (cs.opacity && parseFloat(cs.opacity) < 1) ||
        (cs.filter && cs.filter !== 'none') ||
        (cs.willChange && cs.willChange.indexOf('transform') !== -1) ||
        cs.isolation === 'isolate';
      if (createsContext && node.parentElement) {
        escapeParent = node.parentElement;
      }
      node = node.parentElement;
    }
    return escapeParent;
  }

  function processImage(img) {
    if (img.hasAttribute('data-ai-checked')) return;
    img.setAttribute('data-ai-checked', 'true');

    if (isExcluded(img)) return;

    var src = img.currentSrc || img.getAttribute('src') || '';
    if (!AI_PATTERN.test(src)) return;

    // Versteckte Bilder (display: none) werden grundsätzlich übersprungen.
    // Beispiel: manche Swatch-Vorlagen rendern ein per CSS ausgeblendetes
    // <img class="... hidden"> nur als Datenquelle (z. B. für ein Script,
    // das daraus die Farbe ausliest) – dessen width/height-Attribut zeigt
    // dann die volle Originalbildgröße, nicht die (nicht vorhandene)
    // Anzeigegröße, was die Größenprüfung unten sonst austricksen würde.
    if (window.getComputedStyle(img).display === 'none') return;

    // Sehr klein angezeigte Bilder (z. B. Farbmuster/Swatches oder kleine
    // Feature-/Eigenschafts-Icons, die zufällig "-ai" im Dateinamen tragen)
    // werden übersprungen: ein Badge wäre dort weder lesbar noch sinnvoll,
    // das Hauptbild trägt die Kennzeichnung bereits.
    var MIN_TAG_SIZE = 60;
    var displayWidth = img.offsetWidth || parseInt(img.getAttribute('width'), 10) || 0;
    var displayHeight = img.offsetHeight || parseInt(img.getAttribute('height'), 10) || 0;
    if (displayWidth > 0 && displayHeight > 0 && displayWidth < MIN_TAG_SIZE && displayHeight < MIN_TAG_SIZE) {
      return;
    }

    // Den Bild-Container finden. Unterstützt sowohl Dawn/Barcelona-Konvention
    // (.media) als auch Hyper-Theme-Konvention (.media-wrapper), Fallback:
    // direktes Elternelement.
    var mediaBox = img.closest('.media') || img.closest('.media-wrapper') || img.parentElement;
    if (!mediaBox) return;

    // Falls diese Box schon getaggt wurde, nichts weiter tun
    if (mediaBox.getAttribute('data-ai-tagged') === 'true') return;
    mediaBox.setAttribute('data-ai-tagged', 'true');

    // Wie viele Bilder teilen sich diese Box? Bei Themes wie Hyper hat JEDES
    // Bild (Haupt-/Hover-Bild) seine EIGENE, separate Box – im Gegensatz zu
    // Barcelona, wo sich zwei Bilder eine gemeinsame .media-Box teilen.
    var imagesInBox = mediaBox.querySelectorAll('img').length;
    var isPicture = mediaBox.tagName === 'PICTURE';

    var badge = document.createElement('span');
    badge.className = 'ai-tag-badge';
    badge.textContent = 'AI';
    badge.style.borderRadius = '0 0 ' + findMatchingRadius(mediaBox) + ' 0';

    // ZUERST gezielt nach einem echten Overlay-Element suchen (z. B. die
    // Deckkraft-Überlagerung bei Slideshows/Bannern). Das Tag wird dann als
    // letztes Kind im gemeinsamen Elternteil angehängt, wodurch es die
    // Stapelreihenfolge automatisch gewinnt.
    var overlaySharedParent = findOverlaySharedParent(mediaBox);

    if (overlaySharedParent) {
      var overlayBadgeWrapper = document.createElement('span');
      overlayBadgeWrapper.className = 'ai-tag-outer ai-tag-outer--absolute';
      overlayBadgeWrapper.appendChild(badge);
      overlaySharedParent.appendChild(overlayBadgeWrapper);
      return;
    }

    // Andernfalls prüfen, ob ein Vorfahre (z. B. eine Motion-/Animations-/
    // Parallax-Komponente mit CSS-transform) einen eigenen Stacking-Context
    // erzeugt. Falls ja, würde ein Badge dort drin per z-index niemals über
    // ein Geschwister-Element hinausragen können – egal ob Einzelbild- oder
    // geteilte Box.
    var escapeParent = findStackingEscapeParent(mediaBox);

    if (escapeParent && escapeParent !== mediaBox.parentNode) {
      // Bild-/Animations-Struktur bleibt zu 100% unangetastet. Das Badge
      // wird stattdessen separat und unabhängig auf der sicheren, äußeren
      // Ebene angehängt (dort, wo auch ein eventuelles Overlay-Element
      // sitzt), damit es nicht mehr dahinter verschwindet.
      var floatingWrapper = document.createElement('span');
      floatingWrapper.className = 'ai-tag-outer ai-tag-outer--absolute';
      floatingWrapper.appendChild(badge);
      escapeParent.appendChild(floatingWrapper);
      return;
    }

    if (imagesInBox <= 1 && !isPicture) {
      // EINZELBILD-BOX: Badge direkt als Kind der Box einfügen. Dadurch erbt
      // es automatisch deren Sichtbarkeits-/Hidden-Zustand (z. B. bei
      // Hover-Bildwechsel-Boxen, die per Klasse/Opacity ein-/ausgeblendet
      // werden) – kein Risiko für Sibling-CSS, da kein zweites Bild in
      // derselben Box steckt.
      var singleBoxPosition = window.getComputedStyle(mediaBox).position;
      if (singleBoxPosition === 'static') {
        mediaBox.classList.add('ai-tag-relative-host');
      }
      mediaBox.appendChild(badge);
    } else {
      // GETEILTE BOX (mehrere Bilder als Geschwister) ODER <picture>-Element:
      // Neuen Wrapper NUR außen um die Box legen. Die Box selbst (inkl.
      // aller Kind-Bilder, Reihenfolge, Klassen) wird unverändert
      // hineinverschoben – dadurch bleiben sämtliche Sibling-/Hover-CSS-
      // Regeln des Themes intakt, und <picture> bekommt keine ungültigen
      // Kind-Elemente (nur <source>/<img>/Script-Elemente sind dort erlaubt).
      var outerParent = mediaBox.parentNode;
      if (!outerParent) return;

      // Prüfen, ob die Box aus dem Dokumentfluss genommen ist (z. B. Hero-Banner
      // mit position: absolute, um Text-Elemente nicht zu verschieben).
      var mediaComputedPosition = window.getComputedStyle(mediaBox).position;
      var isOutOfFlow = mediaComputedPosition === 'absolute' || mediaComputedPosition === 'fixed';

      var outerWrapper = document.createElement('span');
      outerWrapper.className = isOutOfFlow ? 'ai-tag-outer ai-tag-outer--absolute' : 'ai-tag-outer';
      outerParent.insertBefore(outerWrapper, mediaBox);
      outerWrapper.appendChild(mediaBox);
      outerWrapper.appendChild(badge);
    }
  }

  // Scannt EIN gegebenes Root-Element (z. B. document beim initialen Scan,
  // oder einen einzelnen neu hinzugefügten Knoten bei späteren Updates)
  function scanRoot(root) {
    if (!root) return;
    // Erlaubt: Element (1), Document (9), DocumentFragment (11).
    // Vorher wurde nur nodeType 1 akzeptiert – dadurch lief der initiale
    // Scan über "document" (nodeType 9) nie, und es wurde kein Bild geprüft.
    if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;

    if (root.nodeType === 1 && root.tagName === 'IMG') {
      processImage(root);
      return;
    }

    if (typeof root.querySelectorAll !== 'function') return;
    var images = root.querySelectorAll('img:not([data-ai-checked])');
    for (var i = 0; i < images.length; i++) {
      processImage(images[i]);
    }
  }

  // Initialer Scan über das gesamte Dokument (einmalig)
  document.addEventListener('DOMContentLoaded', function () {
    scanRoot(document);
  });

  // Sammelt neu hinzugefügte Knoten aus mehreren Mutations-Bursts und
  // verarbeitet sie gebündelt in einem Leerlaufmoment des Browsers,
  // statt bei jeder einzelnen DOM-Änderung sofort zu reagieren.
  var pendingNodes = [];
  var scanScheduled = false;

  function flushPendingNodes() {
    scanScheduled = false;
    var nodes = pendingNodes;
    pendingNodes = [];
    for (var i = 0; i < nodes.length; i++) {
      scanRoot(nodes[i]);
    }
  }

  var observer = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var n = 0; n < added.length; n++) {
        if (added[n].nodeType === 1) {
          pendingNodes.push(added[n]);
        }
      }
    }

    if (pendingNodes.length && !scanScheduled) {
      scanScheduled = true;
      scheduleIdle(flushPendingNodes);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();